import * as React from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { observer } from 'mobx-react'

import type ReportStruct from 'modules/reports/lib/ReportStruct'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'

import styles from './index.module.scss'

interface Props {
  munched: any
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  chartData: Record<string, any>
}

/**
 * NumericMetric uses the data structure from the Pie muncher to render the chart.
 *
 * The NumericMetric muncher uses the projection primaryField of the Query class in
 * order get the projection key and use it to get the value from the data.
 *
 * @example
 * // 1
 *
 * const data = {
 *  count.leave.id: 1, // firstProjection
 * }
 * const aggregation = {
 *  function: "count",
 *  path: "leave.id"
 * }
 * const projection = {
 *  function: "count",
 *  path: "leave.id"
 * }
 *
 * const muncher = new NumericMetric({data, aggregation, projection})
 * muncher.call()
 *
 * @param munched - provide data structure output from the Line muncher class
 * @param data - provide raw data from reports controller
 * @param reportStruct - provide reportStruct in order to read the query projections
 * @param chartData - provide chartData in order to read the sql projections
 */
const NumericMetric: React.FC<Props> = ({ munched, reportStruct }) => {
  /**
   * projection - first report projection
   * @returns the first report projection
   */
  const projection: ProjectionParams | undefined = React.useMemo(
    () => reportStruct.props.projections[0],
    [reportStruct.props.projections]
  )

  /**
   * value - returns the munched data.
   */
  const value: string | null | undefined = React.useMemo(() => munched, [munched])

  /**
   * calculateFontSize
   *
   * This function creates a canvas with the text to render to calculate
   * the font size that fits within the available bounds.
   *
   * @param value - numeric value
   * @param width - available width
   * @param height - available height
   * @returns fontsize for the available space
   */
  const calculateFontSize = React.useCallback(
    (value: string, width: number, height: number): number => {
      const sampleFontSize = 10
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      if (!context) {
        return sampleFontSize
      }

      context.font = `bold ${sampleFontSize}px "Inter", sans-serif`
      const metrics = context.measureText(value)
      const calculatedHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
      const calculatedWidth = context.measureText(value).width

      if (calculatedHeight == 0 || calculatedWidth == 0) {
        return sampleFontSize
      }

      const ratio = Math.min(width / calculatedWidth, height / calculatedHeight)
      return sampleFontSize * ratio
    },
    []
  )

  const removeTrailDecimal = React.useCallback((value: number): number => {
    const integerPart = Math.trunc(value)

    if (value - integerPart == 0) {
      return value
    }

    return Math.round(value * 100) / 100
  }, [])

  if (!value) return null

  return (
    <AutoSizer>
      {({ height, width }: any) => {
        const gap = 0.9

        const formattedValue =
          typeof value == 'string' ? value : removeTrailDecimal(parseFloat(value))
        const pixelSize = calculateFontSize(value, width, height) * gap

        const style = {
          fontSize: `${pixelSize}px`,
        }

        return (
          <div
            className={styles.content}
            style={{
              height,
              width,
            }}
          >
            <span className={styles.value} style={style}>
              {formattedValue}
            </span>
          </div>
        )
      }}
    </AutoSizer>
  )
}

export default observer(NumericMetric)
